import React, { useEffect, useState } from 'react';

export interface TabProps {
  label: string; // title of tab
}

export interface TabsProps {
  children: any;
  onTabChange?: Function; // Callback function triggered when a tab is changed.
  rootClass?: string; // Additional CSS class for the root container of Tabs.
  tabClass?: string; // Additional CSS class for each individual tab.
  selectedIndex?: number; // Optional controlled index; keeps the active tab in sync with parent state.
  defaultIndex?: number; // Initial selected tab index (uncontrolled). Defaults to 0.
}

/**
 * Tabs Component
 * @param {TabsProps} props - The properties for the Tabs component.
 * @returns {JSX.Element} - The rendered Tabs component.
 *
 * @usage
 * const MyTabs = () => {
 *   // Callback function for tab change
 *   const handleTabChange = (index: number) => {
 *     console.log(`Selected tab index: ${index}`);
 *   };
 *
 *   return (
 *     <Tabs onTabChange={handleTabChange} >
 *       <Tab label="Tab 1" />
 *       <Tab>
 *         {renderCustomTab()}
 *       </Tab>
 *     </Tabs>
 *   );
 * };
 */
const Tabs: React.FC<TabsProps> = ({
  children,
  onTabChange,
  rootClass = 'w-full',
  tabClass = '',
  selectedIndex,
  defaultIndex = 0,
}) => {
  const [selectedTabIndex, setSelectedTabIndex] = useState(selectedIndex ?? defaultIndex);

  // Follow the parent when used as a controlled component (e.g. switching tabs programmatically).
  useEffect(() => {
    if (selectedIndex !== undefined) {
      setSelectedTabIndex(selectedIndex);
    }
  }, [selectedIndex]);

  const handleTabChange = (index: number) => {
    setSelectedTabIndex(index);
    onTabChange && onTabChange(index);
  };

  return (
    <div className={`flex justify-around ${rootClass}`}>
      {React.Children.map(children, (child, index) => {
        if (!React.isValidElement(child)) return null; // add this guard
        return React.cloneElement(child as any, {
          index,
          selected: index === selectedTabIndex,
          onClick: handleTabChange,
          className: tabClass,
        });
      })}
    </div>
  );
};

export default Tabs;
